import type { FafData, SlotDef } from '../core/types.js';
import { SLOTS, appTypeUsesSlot } from '../core/slots.js';
import { FAF_VERSION } from '../core/version.js';
import {
  detectFrameworks,
  detectLanguage,
  detectPolyglotLanguage,
  detectSubdirStacks,
  detectProjectTypeWithRationale,
  detectRuntime,
  detectPackageManager,
  detectCicd,
  detectHosting,
  detectBuildTool,
  detectSvelteAdapter,
  readPackageJson,
  detectKeyFiles,
  detectCommands,
  detectTechStack,
} from './scanner.js';

/** The major version of the `svelte` dependency package.json names
 *  (`^5.0.0` → 5), or NaN when it names none or no number. */
function svelteMajor(pkg: ReturnType<typeof readPackageJson>): number {
  const range = pkg?.dependencies?.svelte ?? pkg?.devDependencies?.svelte;
  const major = typeof range === 'string' ? /\d+/.exec(range)?.[0] : undefined;
  return major === undefined ? NaN : Number(major);
}

/** Detect the project stack from repo facts and build .faf data. A slot with
 *  no evidence is left empty (`''`) — never a default. */
export function detectStack(dir: string): FafData {
  return detectStackWithFacts(dir).data;
}

/**
 * {@link detectStack}'s data, and `facts`: the repo's fact for every stack
 * slot (by field — `runtime`, `database`), whatever the app-type — `''` when
 * the repo shows none. detectStack marks a slot its detected type leaves out
 * `slotignored`; `faf auto` fills such a slot from `facts` when the file's own
 * type uses it (a `backend` project whose repo reads as a CLI still gets
 * `runtime: Go` from go.mod).
 */
export function detectStackWithFacts(dir: string): { data: FafData; facts: Record<string, string> } {
  const pkg = readPackageJson(dir);
  const frameworks = detectFrameworks(dir);
  const detection = detectProjectTypeWithRationale(dir);
  const projectType = detection.type;
  // Polyglot repos (real stack in sibling dirs) get an aggregated language
  // string instead of the misleading root-only detectLanguage() fallback.
  const isPolyglot = detection.found.some(f => f.startsWith('polyglot:'));
  const language = (isPolyglot && detectPolyglotLanguage(dir)) || detectLanguage(dir);
  const runtime = detectRuntime(dir);
  const pkgManager = detectPackageManager(dir);
  const cicd = detectCicd(dir);
  const hosting = detectHosting(dir);
  const buildTool = detectBuildTool(dir);

  // Find specific frameworks by category
  const frontendFw = frameworks.find(f => f.category === 'frontend');
  const cssFw = frameworks.find(f => f.category === 'css');
  const uiFw = frameworks.find(f => f.category === 'ui');
  const stateFw = frameworks.find(f => f.category === 'state');
  const backendFw = frameworks.find(f => f.category === 'backend');
  const dbFw = frameworks.find(f => f.category === 'database');

  // Which slots the app-type uses (an unknown type reads as library)
  const uses = (slot: SlotDef): boolean => appTypeUsesSlot(projectType, slot) ?? appTypeUsesSlot('library', slot) === true;

  // Framework sub-type detection (framework → svelte, framework → nextjs, etc.)
  const isFramework = projectType === 'framework';
  const hasSvelteSignal = frameworks.some(f => f.slug === 'svelte' || f.slug === 'sveltekit');
  const frameworkSubType = isFramework && hasSvelteSignal ? 'svelte' : (isFramework ? null : null);

  // Svelte-aware: fires for svelte apps AND framework→svelte repos
  const isSvelte = projectType === 'svelte' || frameworkSubType === 'svelte';
  const svelteAdapter = isSvelte ? detectSvelteAdapter(dir) : null;
  const hasSvelteKit = isSvelte && frameworks.some(f => f.slug === 'sveltekit');

  // The repo's fact for each stack slot, whatever the app-type ('' when none).
  const facts: Record<string, string> = {};
  const stackSlots = SLOTS.filter(s => s.path.startsWith('stack.'));

  for (const slot of stackSlots) {
    const field = slot.path.replace('stack.', '');
    // Detected values (Svelte-aware overrides applied inline)
    switch (field) {
      case 'frontend': facts[field] = frontendFw?.name ?? ''; break;
      case 'css_framework': facts[field] = cssFw?.name ?? ''; break;
      case 'ui_library': facts[field] = uiFw?.name ?? ''; break;
      case 'state_management':
        // Svelte 5 uses Runes — no external state library needed. Only when
        // package.json says Svelte 5 or later: Svelte 4 has no Runes.
        facts[field] = stateFw?.name ?? (isSvelte && svelteMajor(pkg) >= 5 ? 'Runes' : '');
        break;
      case 'backend':
        // SvelteKit IS the backend (server routes, form actions, hooks)
        facts[field] = isSvelte ? (hasSvelteKit ? 'SvelteKit' : (backendFw?.name ?? '')) : (backendFw?.name ?? '');
        break;
      case 'api_type':
        // SvelteKit uses form actions + server routes
        facts[field] = hasSvelteKit ? 'Server Routes' : '';
        break;
      case 'runtime': facts[field] = runtime; break;
      case 'database':
        // Only populate if ORM actually detected
        facts[field] = dbFw?.name ?? '';
        break;
      case 'connection':
        facts[field] = dbFw?.name ?? '';
        break;
      case 'hosting':
        // Svelte adapter → hosting platform (adapter-vercel = Vercel, etc.)
        if (isSvelte && svelteAdapter) {
          facts[field] = svelteAdapter;
        } else {
          facts[field] = hosting ?? '';
        }
        break;
      case 'build':
        // SvelteKit always builds with Vite; plain Svelte only when the repo says so
        facts[field] = hasSvelteKit ? 'Vite' : (buildTool ?? '');
        break;
      case 'cicd': facts[field] = cicd ?? ''; break;
      case 'package_manager': facts[field] = pkgManager; break;
      default: facts[field] = ''; break;
    }
  }

  // Build stack with slotignored for the slots the app-type leaves out
  // (a framework repo also leaves out the slots framework source code never
  // has — see appTypeUsesSlot)
  const stack: Record<string, string> = {};
  for (const slot of stackSlots) {
    const field = slot.path.replace('stack.', '');
    stack[field] = uses(slot) ? facts[field] : 'slotignored';
  }

  // Build monorepo section
  const monorepo: Record<string, string> = {};
  const monorepoSlots = SLOTS.filter(s => s.path.startsWith('monorepo.'));
  for (const slot of monorepoSlots) {
    const field = slot.path.replace('monorepo.', '');
    if (!uses(slot)) {
      monorepo[field] = 'slotignored';
    } else {
      monorepo[field] = '';
    }
  }

  // Build project section
  const project: Record<string, string> = {
    name: pkg?.name ?? dir.split('/').pop() ?? 'project',
    // A polyglot repo's root package.json describes the tooling shell, not the
    // product — leave goal for the README interrogator / the human to fill.
    goal: isPolyglot ? '' : (pkg?.description ?? ''),
    main_language: language,
    type: projectType,
  };
  if (isFramework && frameworkSubType) {
    project.framework = frameworkSubType;
  }

  // Canonical FAFb v2 chunks (read by faf-fafb compile).
  // Without these keys the brick still compiles, but only from whatever
  // other canonical fields are present. Per v6.6.md + faf-auto-no-guess-no-slop:
  // tech_stack/key_files/commands auto-populate from observable signals;
  // architecture/context stay empty (user fill — architecture overlaps with
  // human_context.how, context is free-form additional signal).
  // Feed detectTechStack the ROOT language (a single token), never the polyglot
  // summary string — that belongs only in project.main_language.
  const techStack = detectTechStack(dir, pkg, frameworks, isPolyglot ? detectLanguage(dir) : language);
  if (isPolyglot) {
    // Add each sibling component's language + top framework (deduped, order-stable).
    for (const s of detectSubdirStacks(dir)) {
      for (const t of [s.language, s.topFramework]) {
        if (t && !techStack.includes(t)) {techStack.push(t);}
      }
    }
  }
  const keyFiles = detectKeyFiles(dir);
  const commands = detectCommands(dir, pkg);

  const result: FafData = {
    faf_version: FAF_VERSION,
    project,
    stack,
    human_context: {
      who: '',
      // A polyglot root's package.json describes the tooling shell, not the
      // product — leave `what` for the README interrogator / the human.
      what: isPolyglot ? '' : (pkg?.description ?? ''),
      why: '',
      where: '',
      when: '',
      how: '',
    },
    monorepo,
    // Runtime-only meta. Stripped before YAML serialization by writeFaf,
    // which uses _meta.found to inject `# found: <list>` next to `type:`.
    // Glass Hood doctrine — the user sees WHY the cli classified as it did.
    _meta: { found: detection.found },
  };

  // Only emit FAFB top-level keys if non-empty (no slop / empty-list pollution)
  if (techStack.length > 0) {result.tech_stack = techStack;}
  if (keyFiles.length > 0) {result.key_files = keyFiles;}
  if (Object.keys(commands).length > 0) {result.commands = commands;}

  return { data: result, facts };
}
